import { Button, DatePicker, Form, message, Popconfirm, Typography } from "antd"
import dayjs from "dayjs"
import { useEffect, useMemo, useState } from "react"

import { semesterCalendarAtoms } from "#atoms"
import Flex from "#blocks/Flex"
import Firebase from "#classes/Firebase"
import { RESOURCES, USER_ACTIONS } from "#customTypes/composedTypes/authTypes"
import { DOC_TYPES } from "#customTypes/composedTypes/firebaseTypes"
import { useAllDocs } from "#hooks/jotai/useSubscription"
import useHasPermission from "#hooks/newHooks/useHasPermission"
import {
  appTodayStamp,
  calendarFromDocs,
  dayBeforeStart,
  dropTrailingBlankSemesters,
  isLockedSemesterStart,
  nextSemesterId,
  SEMESTER_CALENDAR_DATE_FORMAT,
  type SemesterCalendarDoc,
  semesterSaveError,
  type SemesterStart,
} from "#utils/semesterCalendar"
import { formatSemester } from "#utils/semesterHelpers"

type StartEdit = {
  startDate: string
  basedOn: string
}

type ShownSemester = SemesterStart & {
  locked: boolean
}

const SEMESTER_SLOTS = ["A", "B", "C"] as const

type YearRow = {
  year: number
  slots: Array<ShownSemester | null>
}

// One row per calendar year, A then B then C. Newest years come first so the
// terms someone is about to edit sit at the top of the scroll area.
const groupSemestersByYear = (semesters: ShownSemester[]): YearRow[] => {
  const byYear = new Map<number, Array<ShownSemester | null>>()
  for (const semester of semesters) {
    const [yearText, letter] = semester.id.split(" ")
    const year = Number(yearText)
    const slot = SEMESTER_SLOTS.indexOf(
      letter as (typeof SEMESTER_SLOTS)[number]
    )
    if (!Number.isInteger(year) || slot < 0) continue
    const slots = byYear.get(year) ?? [null, null, null]
    slots[slot] = semester
    byYear.set(year, slots)
  }
  return [...byYear.entries()]
    .sort(([yearA], [yearB]) => yearB - yearA)
    .map(([year, slots]) => ({ year, slots }))
}

const endLabelFor = (semesters: ShownSemester[], id: string): string => {
  const index = semesters.findIndex((row) => row.id === id)
  const nextStart = semesters[index + 1]?.startDate
  if (!nextStart) return "Ends when the next one is added"
  return `Ends ${dayBeforeStart(nextStart)}`
}

// The list is the live subscription plus edits that still match it. A draft
// whose live start has moved is dropped, so a save or another writer cannot
// leave a stale row on screen.
const shownSemesters = (
  liveRows: SemesterStart[],
  edits: Record<string, StartEdit>,
  added: SemesterStart[],
  removedIds: Set<string>,
  today: string
): ShownSemester[] => {
  const liveIds = new Set(liveRows.map((row) => row.id))
  const merged = liveRows
    .filter((row) => !removedIds.has(row.id))
    .map((row) => {
      const locked = isLockedSemesterStart(row.startDate, today)
      const edit = edits[row.id]
      if (locked || !edit || edit.basedOn !== row.startDate) {
        return { ...row, locked }
      }
      return { id: row.id, startDate: edit.startDate, locked: false }
    })
  const extras = added
    .filter((row) => !liveIds.has(row.id) && !removedIds.has(row.id))
    .map((row) => ({ ...row, locked: false }))
  return [...merged, ...extras]
}

const lockedRowChanged = (
  liveRows: SemesterStart[],
  shown: SemesterStart[],
  today: string
): boolean =>
  liveRows.some((live) => {
    if (!isLockedSemesterStart(live.startDate, today)) return false
    const row = shown.find((item) => item.id === live.id)
    return row == null || row.startDate !== live.startDate
  })

const SemesterCalendarSettings = () => {
  const canUpdate = useHasPermission(RESOURCES.SEMESTER, USER_ACTIONS.UPDATE)
  const docs = useAllDocs<SemesterCalendarDoc>(
    semesterCalendarAtoms,
    DOC_TYPES.SEMESTERS
  )
  const liveRows = useMemo(
    () => calendarFromDocs(docs)?.semesters ?? null,
    [docs]
  )
  const today = appTodayStamp()
  const [edits, setEdits] = useState<Record<string, StartEdit>>({})
  const [added, setAdded] = useState<SemesterStart[]>([])
  const [removed, setRemoved] = useState<string[]>([])
  const [saveError, setSaveError] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    if (!liveRows) return
    setEdits((current) => {
      const ids = Object.keys(current)
      if (ids.length === 0) return current
      const next: Record<string, StartEdit> = {}
      let changed = false
      for (const id of ids) {
        const edit = current[id]
        const live = liveRows.find((row) => row.id === id)
        if (
          live &&
          live.startDate === edit.basedOn &&
          !isLockedSemesterStart(live.startDate, today)
        ) {
          next[id] = edit
        } else {
          changed = true
        }
      }
      return changed ? next : current
    })
    setAdded((current) => {
      if (current.length === 0) return current
      const liveIds = new Set(liveRows.map((row) => row.id))
      const next = current.filter((row) => !liveIds.has(row.id))
      return next.length === current.length ? current : next
    })
    setRemoved((current) => {
      if (current.length === 0) return current
      const liveIds = new Set(liveRows.map((row) => row.id))
      const next = current.filter((id) => liveIds.has(id))
      return next.length === current.length ? current : next
    })
  }, [liveRows, today])

  const removedIds = useMemo(() => new Set(removed), [removed])
  const shown = useMemo(
    () =>
      liveRows
        ? shownSemesters(liveRows, edits, added, removedIds, today)
        : null,
    [liveRows, edits, added, removedIds, today]
  )
  const saveProblem = shown
    ? semesterSaveError(shown.map(({ id, startDate }) => ({ id, startDate })))
    : null
  const blockingId = saveProblem?.id ?? null
  const blockingMessage = saveProblem
    ? saveProblem.kind === "order"
      ? `${formatSemester(saveProblem.id)} starts before the previous semester`
      : `${formatSemester(saveProblem.id)} needs a start date`
    : null
  const pastLockedChanged =
    shown != null &&
    liveRows != null &&
    lockedRowChanged(liveRows, shown, today)

  const handleStartChange = (id: string, startDate: string) => {
    const live = liveRows?.find((row) => row.id === id)
    if (live && isLockedSemesterStart(live.startDate, today)) return
    if (!live) {
      setAdded((current) =>
        current.map((row) => (row.id === id ? { ...row, startDate } : row))
      )
      return
    }
    setEdits((current) => ({
      ...current,
      [id]: {
        startDate,
        basedOn: current[id]?.basedOn ?? live.startDate,
      },
    }))
  }

  const handleAddNext = () => {
    const last = shown?.at(-1)
    if (!last) return
    setAdded((current) => {
      const liveIds = new Set(liveRows?.map((row) => row.id))
      const pending = current.filter((row) => !liveIds?.has(row.id))
      return [...pending, { id: nextSemesterId(last.id), startDate: "" }]
    })
  }

  const handleRemoveLast = () => {
    const last = shown?.at(-1)
    if (!last || last.locked || (shown?.length ?? 0) <= 1) return
    const alreadySaved = liveRows?.some((row) => row.id === last.id) ?? false
    if (alreadySaved) {
      setRemoved((current) =>
        current.includes(last.id) ? current : [...current, last.id]
      )
      setEdits((current) => {
        if (!current[last.id]) return current
        const next = { ...current }
        delete next[last.id]
        return next
      })
      return
    }
    setAdded((current) => current.filter((row) => row.id !== last.id))
  }

  const handleSave = async () => {
    if (!shown || !liveRows) return
    const dates = shown.map(({ id, startDate }) => ({ id, startDate }))
    const payload = dropTrailingBlankSemesters(dates)
    if (
      semesterSaveError(dates) ||
      lockedRowChanged(liveRows, payload, today)
    ) {
      return
    }
    const removedPersisted = removed.filter((id) =>
      liveRows.some((row) => row.id === id)
    )
    setIsSaving(true)
    setSaveError(null)
    try {
      await Firebase.getInstance().saveSemesterCalendarDocs(
        payload,
        removedPersisted
      )
      setAdded((current) =>
        current.filter((row) => payload.some((saved) => saved.id === row.id))
      )
      void message.success("Semester start dates saved")
    } catch (error) {
      const text = error instanceof Error ? error.message : "Save failed"
      setSaveError(text)
      console.error("Semester start date save failed", error)
    } finally {
      setIsSaving(false)
    }
  }

  const yearRows = useMemo(
    () => (shown ? groupSemestersByYear(shown) : []),
    [shown]
  )

  if (!shown?.length) return null

  const blockingRowShown = shown.some((row) => row.id === blockingId)
  const last = shown.at(-1)
  const lastIsRemovable = last != null && !last.locked && shown.length > 1
  const lastAlreadySaved =
    last != null && (liveRows?.some((row) => row.id === last.id) ?? false)
  const removeLastButton = (
    <Button
      size="large"
      disabled={!lastIsRemovable || isSaving}
      onClick={lastAlreadySaved ? undefined : handleRemoveLast}
    >
      Remove last semester
    </Button>
  )

  return (
    <Form
      colon={false}
      layout="vertical"
      requiredMark={false}
      style={{
        alignItems: "center",
        display: "flex",
        flex: "1 1 auto",
        flexDirection: "column",
        gap: 12,
        minHeight: 0,
        width: "100%",
      }}
    >
      <p style={{ margin: 0 }}>
        Set when each semester starts. It ends the day before the next one
        starts.
      </p>
      {canUpdate ? (
        <Flex gap={8}>
          <Button size="large" onClick={handleAddNext}>
            Add next semester
          </Button>
          {lastIsRemovable && lastAlreadySaved && last ? (
            <Popconfirm
              title={`Remove ${formatSemester(last.id)}?`}
              description="This semester is already saved. It will be deleted when you save start dates."
              okText="Remove"
              cancelText="Keep"
              okButtonProps={{ danger: true }}
              onConfirm={handleRemoveLast}
            >
              {removeLastButton}
            </Popconfirm>
          ) : (
            removeLastButton
          )}
          <Button
            size="large"
            type="primary"
            disabled={blockingId != null || pastLockedChanged || isSaving}
            loading={isSaving}
            onClick={() => {
              void handleSave()
            }}
          >
            Save start dates
          </Button>
        </Flex>
      ) : null}
      {blockingMessage && !blockingRowShown ? (
        <Typography.Text type="danger">{blockingMessage}</Typography.Text>
      ) : null}
      {saveError ? (
        <Typography.Text type="danger">{saveError}</Typography.Text>
      ) : null}
      <Flex
        col
        gap={20}
        style={{
          flex: "1 1 auto",
          minHeight: 0,
          width: "100%",
          overflow: "auto",
          alignItems: "center",
          marginTop: 20,
          paddingBottom: 12,
        }}
      >
        {yearRows.map((year) => (
          <div
            key={year.year}
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 240px)",
              gap: 24,
              alignItems: "start",
            }}
          >
            {year.slots.map((row, slot) =>
              row ? (
                <Form.Item
                  key={row.id}
                  label={formatSemester(row.id)}
                  validateStatus={row.id === blockingId ? "error" : undefined}
                  help={
                    row.id === blockingId
                      ? blockingMessage
                      : endLabelFor(shown, row.id)
                  }
                  style={{ marginBottom: 0 }}
                >
                  <DatePicker
                    allowClear={false}
                    disabled={!canUpdate || row.locked}
                    format={SEMESTER_CALENDAR_DATE_FORMAT}
                    style={{ width: "100%" }}
                    value={
                      row.startDate
                        ? dayjs(
                            row.startDate,
                            SEMESTER_CALENDAR_DATE_FORMAT,
                            true
                          )
                        : null
                    }
                    onChange={(value) => {
                      if (!value || row.locked) return
                      handleStartChange(
                        row.id,
                        value.format(SEMESTER_CALENDAR_DATE_FORMAT)
                      )
                    }}
                  />
                </Form.Item>
              ) : (
                <div key={`${year.year}-${SEMESTER_SLOTS[slot]}`} />
              )
            )}
          </div>
        ))}
      </Flex>
    </Form>
  )
}

export default SemesterCalendarSettings
